import { WorkPage } from '../components/work-page.tsx';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalizedContent } from '../i18n/localized.ts';
import { magicQuoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, TextArea } from '../components/ui.tsx';
import type { MagicQuoteRoom } from '@shared/types/index.ts';
import { useDocumentTitle } from '../hooks/index.ts';
import CaptureTimeline from '../components/magic-quote/capture-timeline.tsx';
import ObservationList from '../components/magic-quote/observation-list.tsx';
import MagicQuoteCustomerCard from '../components/magic-quote/customer-card.tsx';
import MagicQuoteRoomCheck, { isMagicQuoteCoverageProtocolValid } from '../components/magic-quote/room-check.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { templateApi } from '../api/endpoints.ts';
import { Select, TextInput } from '../components/ui.tsx';
import AddressLink from '../components/address-link.tsx';

/**
 * What the estimator sees once the walkthrough is over: what was found, what
 * needs a person's eye, and the draft quote it becomes.
 *
 * The order on this page is the argument the feature makes. Readiness and the
 * items needing review come *first* — before the button that produces a
 * price — because the entire point of extracting claims with confidences is
 * that somebody looks at the shaky ones before they cost a customer money.
 * Generation is never blocked on it, though: a cleaner who wants a quote out
 * of four photos and one sentence is allowed to have one, and the score is
 * there to tell them what they are trading away.
 */
export default function MagicQuoteReviewPage() {
  const { t } = useTranslation();
  const { sessionId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  useDocumentTitle(t('magicQuote.review.documentTitle'));

  const [error, setError] = useState<string | null>(null);
  // The nonce is what makes "Show me" work twice. The timeline scrolls to the
  // capture when the value it is handed CHANGES, so clicking the same link
  // again — after scrolling away to read something else — produced an
  // identical object and therefore nothing at all.
  const [openEvidence, setOpenEvidence] = useState<{
    captureId: string;
    offsetSeconds: number | null;
    nonce: number;
  } | null>(null);
  const showEvidence = (evidence: { captureId: string; offsetSeconds: number | null }) =>
    setOpenEvidence({ ...evidence, nonce: Date.now() });
  const [question, setQuestion] = useState('');
  const [citedIds, setCitedIds] = useState<string[]>([]);
  /** When this page started waiting for the walkthrough to be organized. */
  const [waitingSince] = useState(() => Date.now());
  /**
   * Set when the estimator asks to see the page anyway.
   *
   * Withholding the page is right while the walkthrough is genuinely being
   * read, and wrong the moment that stops being true without anybody being
   * told. A run that died without unwinding — the screen closed while it
   * worked — leaves its claim behind until the lease lapses, and the page has
   * no way to tell that from a run still going: both say "organizing". So the
   * wait is never a wall. After a minute it offers the way past itself, which
   * is the whole difference between waiting and being stuck.
   */
  const [showAnyway, setShowAnyway] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  /**
   * Why the capture queue stopped moving, when something said why.
   *
   * The queue is poked every eight seconds while anything is unread, and until
   * this existed the answer was thrown away: a pass refused for want of AI
   * credits looked exactly like a pass that had not finished yet, so the page
   * kept asking, the count kept not moving, and the wait below never ended.
   * Reported as the walkthrough being stuck with no error at all — from a
   * company that had spent its day's share of the month.
   *
   * Cleared by the first pass that is not refused, which is what makes the
   * message honest. It promises that reading resumes on its own, and the day's
   * cap can lift while this page sits open — the window rolls, somebody
   * upgrades the plan — so the page keeps asking on a slow clock and takes the
   * notice down the moment the queue starts moving again.
   */
  const [queueStalled, setQueueStalled] = useState<string | null>(null);

  const session = useQuery({
    queryKey: ['magic-quote', sessionId],
    queryFn: () => magicQuoteApi.get(sessionId),
    enabled: Boolean(sessionId),
    // Captures may still be processing when the cleaner reaches this page, and
    // the reconciliation after them is what this page is waiting for. Polling
    // stops once there is something to show — see `waiting` below.
    refetchInterval: (query) => {
      const state = query.state.data;
      if (!state) return false;
      if ((state.pendingCaptureCount ?? 0) > 0) return 6000;
      // A pass in flight is also something to wait for: the page withholds
      // itself while one runs, so it has to keep asking whether it has landed.
      if (state.organizing) return 5000;
      return state.summary || state.quoteId ? false : 5000;
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['magic-quote', sessionId] });

  /**
   * What the last "Organize again" did, said beside the button that did it.
   *
   * It used to say nothing at all. The press spins for as long as the model
   * takes and then the screen settles back to looking exactly as it did — and
   * on a second pass over the same walkthrough it usually IS the same, so the
   * only honest reading of the button was that it did nothing. Worse, the two
   * ways it can decline — a reconciliation already running, a walkthrough that
   * has since produced a quote — were reported in the page-level alert strip
   * above, which on a phone is off the top of the screen by the time somebody
   * has scrolled to the button.
   */
  const [organized, setOrganized] = useState<{ ok: boolean; message: string } | null>(null);

  /**
   * Whether the pass now running was asked for from the button on this page.
   *
   * Every other pass takes the page away until it lands — see `waiting` below.
   * This one must not: the estimator is looking at the rooms it will replace,
   * pressed the button themselves, and the button says what it is doing.
   */
  const askedToOrganize = useRef(false);

  const resynthesize = useMutation({
    mutationFn: () => magicQuoteApi.synthesize(sessionId),
    // Cleared on the way in, so a retry does not run under the previous
    // pass's refusal. `waiting` below reads `organized`, and a stale not-ok
    // held the page open — Room Check and all — while the new pass was
    // rewriting the rooms that Room Check was asking about.
    onMutate: () => setOrganized(null),
    onSettled: () => {
      askedToOrganize.current = false;
    },
    onSuccess: (result) => {
      setOrganized(
        result.ok
          ? {
              ok: true,
              // `session.data`, not the `data` const below: this callback runs
              // long after render, and a load that bailed early never reached
              // that declaration.
              message: t('magicQuote.review.organizedAgain', {
                score: result.readinessScore ?? session.data?.readinessScore ?? 0,
              }),
            }
          : { ok: false, message: result.reason ?? t('magicQuote.review.organizeFailed') },
      );
      invalidate();
    },
    onError: (cause: Error) => setOrganized({ ok: false, message: cause.message }),
  });

  // Asked before "build anyway" runs, because the draft it makes is final.
  const [generateAnyway, setGenerateAnyway] = useState(false);
  const generate = useMutation({
    mutationFn: (options: { allowIncomplete: boolean }) => magicQuoteApi.generate(sessionId, options),
    onSuccess: (result) => {
      setGenerateAnyway(false);
      // A draft that exists is opened, partial or not: the quote editor is
      // where whatever was left out gets added, and the server has already
      // noted what that is on the draft. Only a response with no draft at
      // all stays here.
      if (typeof result.quoteId !== 'string' || !result.quoteId.trim()) {
        setError(t('magicQuote.review.roomCheckIncomplete'));
        invalidate();
        return;
      }
      navigate(`/quotes/${result.quoteId}`);
    },
    onError: (cause: Error) => {
      setError(cause.message);
      invalidate();
    },
  });

  const confirmCoverage = useMutation({
    mutationFn: (input: { version: number; roomTemplateIds: string[] }) =>
      magicQuoteApi.confirmCoverage(sessionId, {
        version: input.version,
        ...(input.roomTemplateIds.length > 0 ? { roomTemplateIds: input.roomTemplateIds } : {}),
        confirmed: true,
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (cause: Error) => {
      if (cause instanceof ApiRequestError && cause.code === 'MAGIC_QUOTE_COVERAGE_VERSION_CONFLICT') {
        setError(t('magicQuote.review.roomCheckConflict'));
        invalidate();
        return;
      }
      setError(cause.message || t('magicQuote.review.roomCheckSaveError'));
    },
  });

  const ask = useMutation({
    mutationFn: (message: string) => magicQuoteApi.chat(sessionId, message),
    onSuccess: (turn) => {
      setQuestion('');
      setCitedIds(turn.citedCaptureIds);
      invalidate();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  // Asked for, not fetched. Every call spends an action from the month's
  // allowance, and React Query's ordinary staleness rules would re-run it
  // each time the tab regained focus or the connection blipped — an invisible
  // meter running behind a page somebody left open.
  const advice = useMutation({
    mutationFn: () => magicQuoteApi.priceAdvice(sessionId),
    onError: (cause: Error) => setError(cause.message),
  });
  const adviceOffered = Boolean(session.data?.quoteId) && company?.aiSettings?.magicQuote?.pricingSuggestions !== false;

  // Answers arrive on the server whether or not this page is still listening.
  //
  // The app turns focus refetching off deliberately — a single-operator tool
  // has no second person making changes behind your back. This page is the
  // exception, and the reason is the phone. A question takes the model the
  // better part of a minute, in which the screen dims and the browser
  // suspends the request; the reply is written to the thread all the same,
  // but the promise this tab is waiting on never settles, so the transcript
  // sits there looking as though nothing was asked until something remounts
  // the page. Re-reading when the tab comes back is what closes that gap —
  // and it is cheap, because reading a walkthrough spends no AI action.
  useEffect(() => {
    const resync = () => {
      if (document.visibilityState === 'visible') invalidate();
    };
    document.addEventListener('visibilitychange', resync);
    return () => document.removeEventListener('visibilitychange', resync);
    // `invalidate` closes over nothing that changes but the session id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pending = session.data?.pendingCaptureCount ?? 0;
  const organizingNow = Boolean(session.data?.organizing);

  /**
   * Every way the walkthrough can still be mid-analysis.
   *
   * A missing summary was only the first of them. A walkthrough that HAS one
   * and still has captures in the queue is a walkthrough whose rooms are about
   * to change: the drain below fires a fresh pass the moment the last one is
   * read, so the page was showing a Room Check, a room count and a scope for a
   * walkthrough the model had not finished reading — and then replacing all
   * three under whoever was reading them. Confirming that Room Check is
   * confirming an answer to a question nobody had finished asking.
   *
   * A pass in flight counts too, for the same reason, with one exception: the
   * one the estimator asked for from the button on this page
   * (`askedToOrganize`).
   */
  const analysisUnfinished =
    session.data !== undefined &&
    (!session.data.summary || pending > 0 || ((organizingNow || resynthesize.isPending) && !askedToOrganize.current));

  /**
   * Withheld until it is worth showing.
   *
   * This page is the result — what the walk found, what it will cost — and
   * opened too early it showed the truth about an unfinished job in the most
   * misleading way available: no rooms, no summary, nothing needing review.
   * An estimator who arrives here before the model has grouped anything reads
   * that as "the walkthrough found nothing", which is a different and much
   * worse claim than "not finished". They then press Generate and get a
   * refusal about a walkthrough that has rooms in it thirty seconds later.
   *
   * So the page waits, and says what it is waiting for. Four ways out of the
   * wait, because being stuck behind it would be worse than the problem it
   * solves: the reconciliation lands, or it fails (`aiStatus` says so, and the
   * page shows its Organize-again button), or it was refused for a reason the
   * estimator has to see — an exhausted allowance is not something to hide
   * behind a spinner that never resolves — or the refusal came from the
   * capture queue rather than the reconciliation. That last one is what this
   * wait was worst at: the queue is nobody's explicit request, so nothing was
   * carrying its refusal to the screen, and the wait sat over a walkthrough no
   * amount of waiting was going to advance.
   *
   * And a fifth that is not a way out but a way past: `showAnyway`, offered by
   * the wait itself after a minute. Which is why the second hand below follows
   * THIS condition rather than a missing summary — it is what puts that offer
   * on screen, and a wait whose clock never starts is the wall this was
   * written not to be.
   *
   * The hatches say "waiting would be pointless", and they hold the wall down
   * once opened — which was wrong the moment a NEW pass started over a
   * walkthrough that had never produced a result. A retry after a failure, or
   * a pass another tab was running, put the whole page up mid-analysis: a
   * Room Check asking which common rooms were missing from a room list the
   * model had not written yet. While a pass is actually running and there is
   * nothing organized to show, the wait is the truth, whatever an earlier
   * attempt said — `stillAnalyzing` overrides every hatch but `showAnyway`,
   * and the pass ending re-opens them, so nobody is walled in.
   */
  const stillAnalyzing = (organizingNow || resynthesize.isPending) && !session.data?.summary;
  const waiting =
    analysisUnfinished &&
    !session.data?.quoteId &&
    !showAnyway &&
    (stillAnalyzing || (session.data?.aiStatus !== 'failed' && organized?.ok !== false && queueStalled === null));

  // A second hand for the wait above. Only runs while there is a wait: a timer
  // re-rendering this page once a second for the whole time somebody has it
  // open would be a cost with nothing to show for it.
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  // Completion only drains a bounded batch, and a capture whose model call
  // failed retryably goes back to `uploaded`. Polling the read-only session
  // would watch those sit there forever — and since generation now waits for
  // the queue, forever means the Generate button never enables. So the page
  // pushes the queue rather than merely observing it.
  useEffect(() => {
    if (pending === 0) return;
    // Slower once a pass has said the queue cannot move — not stopped.
    //
    // Asking every eight seconds changes nothing about an exhausted daily cap,
    // and it keeps the page looking busy while it is not. But stopping outright
    // would make this page's own promise false: the cap lifts twenty-four hours
    // after it was reached, and an owner can lift it now by upgrading. So the
    // queue is still asked, on a clock sized for the thing being waited on
    // rather than for a capture that is nearly read.
    const interval = queueStalled === null ? 8_000 : 5 * 60_000;
    const timer = window.setInterval(() => {
      magicQuoteApi
        .process(sessionId)
        .then((result) => {
          // `?? null` because a refusal is the presence of a reason, not the
          // absence of `null`: an `undefined` from a response that omitted the
          // field would otherwise be stored as a stall with nothing to say, and
          // every later check would agree the queue was stopped.
          setQueueStalled(result.reason ?? null);
        })
        .catch(() => {
          // The next tick tries again; the count on screen is the real signal.
        });
    }, interval);
    return () => window.clearInterval(timer);
  }, [pending, sessionId, queueStalled]);

  // And when the last one lands, reconcile once more so those captures reach
  // the rooms and claims.
  //
  // Only after a real pending → zero TRANSITION, which is the whole
  // subtlety: the ordinary review load already has a summary and zero
  // pending, so firing on that condition alone would re-organize every
  // walkthrough the moment it was opened — a second paid action, and the
  // estimator's first result replaced before they had read it.
  //
  // Declared after the mutation it fires: a hook reaching backwards for a
  // `const` below it is a temporal-dead-zone crash waiting for the first
  // render that satisfies the condition.
  const sawPending = useRef(false);
  const hasQuote = Boolean(session.data?.quoteId);
  useEffect(() => {
    if (hasQuote) return;
    if (pending > 0) {
      sawPending.current = true;
      return;
    }
    if (!sawPending.current) return;
    sawPending.current = false;
    resynthesize.mutate();
    // The mutation handle is stable across renders; depending on it would
    // re-run this effect every render instead of on the drain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, hasQuote]);

  /**
   * And the walkthrough nobody ever finished.
   *
   * The drain above only fires on a pending → zero TRANSITION, which is the
   * right trigger for captures arriving while somebody watches. It cannot help
   * the estimator who left the walkthrough screen without pressing Finish and
   * came back to this page days later: nothing is pending, nothing has been
   * organized, and there is no transition left to wait for. Withholding the
   * page for a reconciliation that is never coming would be a worse dead end
   * than the empty page it replaced, so this starts one.
   *
   * Once. A pass that comes back refused — an exhausted allowance — sets
   * `organized` and the page stops waiting rather than asking again, which is
   * both what the estimator needs to see and how a walkthrough avoids
   * spending an allowance in a loop.
   */
  const kicked = useRef(false);
  // Only ever true of a walkthrough that has actually been READ. While the
  // session is still loading, `session.data` is undefined, and undefined has
  // no summary, no quote and no failure — which this condition used to read
  // as "never organized". The effect below then fired on the very first
  // commit of every mount, before the answer arrived: every refresh of the
  // review page started a full paid reconciliation of a walkthrough that was
  // already organized, and the rooms on screen were replaced under the
  // estimator a minute later when it landed.
  const unorganized =
    session.data !== undefined && !session.data.summary && !hasQuote && session.data.aiStatus !== 'failed';
  useEffect(() => {
    if (!unorganized || pending > 0 || organizingNow || kicked.current) return;
    if (resynthesize.isPending || organized !== null) return;
    kicked.current = true;
    resynthesize.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unorganized, pending, organizingNow, organized]);

  if (session.isLoading) return <LoadingBlock label={t('magicQuote.review.loading')} />;
  if (session.isError || !session.data) {
    return (
      <Alert tone="error" title={t('magicQuote.review.loadErrorTitle')}>
        {t('magicQuote.review.loadErrorBody')}
      </Alert>
    );
  }

  const data = session.data;
  // Undefined covers an older Worker; null is the intentional legacy rollout
  // boundary. Both preserve pre-coverage behavior until a current GET opts
  // the session into protocol v1.
  const coverage = data.coverage ?? null;
  // `method=manual` because "New quote" opens on whichever way was used last
  // — here, almost certainly Magic Quote — and this link is the decision to
  // do it by hand instead.
  const manualQuoteParams = new URLSearchParams({ method: 'manual' });
  if (data.customerId) manualQuoteParams.set('customerId', data.customerId);
  if (data.propertyId) manualQuoteParams.set('propertyId', data.propertyId);
  const manualQuotePath = `/quotes/new?${manualQuoteParams.toString()}`;

  // Rulings stop being editable once the draft exists: from that moment the
  // priced rooms and the quote's own notes are the record, and a "removed"
  // here would not reach either. The quote editor is where a generated quote
  // is corrected — and the server refuses these anyway, so offering buttons
  // that only produce an error would be the worse half of both options.
  const canReview = can('quotes:write') && !data.quoteId;
  // What holds "Generate quote" for more than a moment. Kept apart from the
  // passing holds (a refetch, an organize in flight) because these are the
  // ones "build anyway" exists to get past.
  const generateBlocked =
    data.pendingCaptureCount > 0 ||
    coverage === null ||
    !isMagicQuoteCoverageProtocolValid(coverage) ||
    !coverage.confirmed ||
    !coverage.canConfirm;
  const live = data.observations.filter((observation) => observation.supersededById === null);
  const needsReview = live.filter((observation) => observation.needsReview);
  const changed = data.observations.filter((observation) => observation.supersededById !== null);

  if (waiting) {
    return (
      <WorkPage
        title={
          data.customerName
            ? t('magicQuote.review.title', { customer: data.customerName })
            : t('magicQuote.review.titleNoCustomer')
        }
        description={data.propertyAddress ? <AddressLink address={data.propertyAddress} /> : undefined}
      >
        <StillReading
          captureCount={data.captures.length}
          pendingCaptureCount={pending}
          organizing={data.organizing || resynthesize.isPending}
          startedAt={waitingSince}
          now={now}
          onShowAnyway={() => setShowAnyway(true)}
        />
      </WorkPage>
    );
  }

  return (
    <WorkPage
      title={
        data.customerName
          ? t('magicQuote.review.title', { customer: data.customerName })
          : t('magicQuote.review.titleNoCustomer')
      }
      description={data.propertyAddress ? <AddressLink address={data.propertyAddress} /> : undefined}
    >
      {/* Before the summary, because it is the one thing on this page that
          has to be settled: the quote is a document addressed to somebody.
          Generating without answering it works too — the walkthrough's own
          suggestion is used — but an estimator should see who that will be
          before they press the button, not afterwards in the quotes list. */}
      {!data.customerId && can('quotes:write') ? (
        <MagicQuoteCustomerCard
          sessionId={sessionId}
          draft={data.customerDraft}
          canCreate={can('customers:write')}
          onAttached={invalidate}
        />
      ) : null}

      {error ? (
        <Alert tone="error" title={t('magicQuote.review.actionErrorTitle')}>
          {error}
          {/* Never the end of the road: the manual quote keeps the customer
              and address even when nothing here can be built. */}
          {!data.quoteId && can('quotes:write') ? (
            <div className="mt-2">
              <Link className="font-semibold underline" to={manualQuotePath}>
                {t('magicQuote.review.roomCheckManualQuote')}
              </Link>
            </div>
          ) : null}
        </Alert>
      ) : null}

      {data.pendingCaptureCount > 0 ? (
        // "Still reading" is only true while something is reading. A queue
        // that has been refused is not slow, it is stopped, and the reason it
        // stopped is the whole message.
        //
        // Said on its own terms, not folded into the walkthrough's status: the
        // failure card below is about an organize that did not finish, and
        // these are captures nothing has been able to read. A walkthrough can
        // be in both states at once, and each sentence answers a different
        // question about it.
        queueStalled !== null ? (
          <Alert tone="warning" title={t('magicQuote.review.queueStalledTitle')}>
            {queueStalled}
          </Alert>
        ) : (
          <Alert tone="info" title={t('magicQuote.review.stillProcessingTitle')}>
            {t('magicQuote.review.stillProcessingBody', { count: data.pendingCaptureCount })}
          </Alert>
        )
      ) : null}

      {coverage ? (
        <MagicQuoteRoomCheck
          coverage={coverage}
          canEdit={can('quotes:write') && !data.quoteId}
          busy={confirmCoverage.isPending}
          manualQuotePath={manualQuotePath}
          onConfirm={(roomTemplateIds) => confirmCoverage.mutate({ version: coverage.version, roomTemplateIds })}
        />
      ) : (
        <div className="mb-6">
          <Alert tone="error" title={t('magicQuote.review.roomCheckProtocolTitle')}>
            {t('magicQuote.review.roomCheckProtocolBody')}
          </Alert>
          <Button className="mt-3" onClick={() => navigate(manualQuotePath)}>
            {t('magicQuote.review.roomCheckManualQuote')}
          </Button>
        </div>
      )}

      <section className="card mb-6">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('magicQuote.review.summaryTitle')}</h2>
          {/* What this one property cost, in the currency the plan is sold in.
              Chat turns and a second organize land here too, so an estimator
              who keeps asking can watch the number move rather than discover
              it at the end of the month. */}
          {data.billedCredits > 0 ? (
            <p className="tabular text-sm text-[color:var(--color-muted)]">
              {t('magicQuote.review.credits', { count: data.billedCredits })}
            </p>
          ) : null}
        </div>

        {/* A bar, because readiness is a judgment about how much of the house
            was actually captured and "62" alone gives an estimator nothing to
            compare it against. The number stays beside it. */}
        {data.readinessScore !== null ? <ReadinessMeter score={data.readinessScore} /> : null}

        {data.summary ? <p className="mt-3 text-sm whitespace-pre-line">{data.summary}</p> : null}

        {/* What happened, when nothing happened.
            A pass that failed leaves this card with no summary and no
            readiness bar, above a walkthrough with no rooms — and the card is
            built to show all three. Silent, that reads as a summary that came
            out half-written rather than as a pass that did not finish, and the
            sentence explaining it only ever existed in the response to the
            request that failed. It survives a reload now, so the estimator is
            told what happened and which button answers it.

            Shown on the status alone, never on the absence of a summary: an
            "Organize again" that fails after a pass that worked leaves the
            EARLIER summary sitting on the row, and reading it as though it
            described the attempt just made is the worse half of both
            outcomes. */}
        {data.aiStatus === 'failed' ? (
          <div className="mt-3">
            <Alert tone="warning" title={t('magicQuote.review.notOrganizedTitle')}>
              {data.aiError ?? t('magicQuote.review.notOrganizedBody')}
            </Alert>
          </div>
        ) : null}

        {data.gaps.length > 0 ? (
          <ul className="mt-3 list-disc pl-5 text-sm text-[color:var(--color-muted)]">
            {data.gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        ) : null}

        {data.openQuestions.length > 0 ? (
          <div className="mt-3 rounded-lg bg-[color:var(--color-surface)] p-3">
            <h3 className="text-sm font-semibold">{t('magicQuote.review.openQuestions')}</h3>
            <ul className="mt-1 list-disc pl-5 text-sm text-[color:var(--color-muted)]">
              {data.openQuestions.map((entry) => (
                <li key={entry}>{entry}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2 border-t border-[color:var(--color-line)] pt-4">
          {can('quotes:write') ? (
            <Button
              onClick={() => {
                askedToOrganize.current = true;
                resynthesize.mutate();
              }}
              busy={resynthesize.isPending}
              // Held while captures are still being read, because that pass
              // holds the same claim this one needs: pressing it here spends
              // nothing and comes back "already being organized", which reads
              // as a button that does not work. The page organizes on its own
              // the moment the queue drains, so there is nothing to lose by
              // waiting — and the notice above says the queue is draining.
              disabled={data.pendingCaptureCount > 0}
            >
              {t('magicQuote.review.resynthesize')}
            </Button>
          ) : null}
          {data.quoteId ? (
            <Link className="btn btn-primary" to={`/quotes/${data.quoteId}`}>
              {t('magicQuote.review.openQuote')}
            </Link>
          ) : can('quotes:write') ? (
            <Button
              variant="primary"
              onClick={() => generate.mutate({ allowIncomplete: false })}
              busy={generate.isPending}
              // Generation stamps the quote onto the walkthrough permanently,
              // and captures still being read cannot contribute to a draft
              // that already exists. Waiting out the queue is the difference
              // between a quote built from the whole walkthrough and one
              // built from the part of it that happened to finish first.
              //
              // The same applies to the reconciliation the queue draining
              // starts: it is rewriting the rooms this button would price,
              // and the moment between "0 pending" and "organized again" is
              // exactly when an impatient estimator taps.
              disabled={generateBlocked || resynthesize.isPending || session.isFetching}
            >
              {t('magicQuote.review.generate')}
            </Button>
          ) : null}
          {/* The way past whatever is holding "Generate quote": captures that
              will not finish reading, a Room Check that cannot be confirmed, a
              room with no price, a refusal. Offered whenever that button
              cannot be pressed or has just failed, and only then, so the
              ordinary path stays the one that checks everything. */}
          {!data.quoteId && can('quotes:write') && (generateBlocked || error !== null) ? (
            <Button onClick={() => setGenerateAnyway(true)} disabled={generate.isPending}>
              {t('magicQuote.review.generateAnyway')}
            </Button>
          ) : null}
        </div>
        {!data.quoteId && can('quotes:write') && generateBlocked ? (
          <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.generateAnywayHint')}</p>
        ) : null}
        {generateAnyway ? (
          <ConfirmDialog
            title={t('magicQuote.review.generateAnywayTitle')}
            confirmLabel={t('magicQuote.review.generateAnyway')}
            busy={generate.isPending}
            error={generate.isError ? generate.error.message : null}
            onConfirm={() => generate.mutate({ allowIncomplete: true })}
            onCancel={() => setGenerateAnyway(false)}
          >
            <p>{t('magicQuote.review.generateAnywayBody')}</p>
          </ConfirmDialog>
        ) : null}

        {/* Said where the button is, and said at all. A model call that takes
            most of a minute needs to explain the wait, and its outcome — a
            reconciliation that ran, or one that was declined because another
            is already in flight — has to land next to the thing that caused
            it rather than in the alert strip at the top of the page. */}
        {resynthesize.isPending ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]" role="status">
            {t('magicQuote.review.organizing')}
          </p>
        ) : organized ? (
          <p
            className={[
              'mt-3 flex items-start gap-1.5 rounded-lg px-3 py-2 text-sm',
              organized.ok
                ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                : 'bg-[color:var(--color-warning-soft)] text-[color:var(--color-warning)]',
            ].join(' ')}
            role="status"
          >
            <span aria-hidden="true">{organized.ok ? '✓' : '⚠'}</span>
            <span>{organized.message}</span>
          </p>
        ) : null}
      </section>

      {/* The engine's number is the one that counts; this sits beside it and
          says so in as many words. */}
      {/* The ask stays until there is advice to show. A request that came back
          without any — out of credits, the model refused, its answer was
          rejected — used to take this card away and put nothing in its place,
          which reads as the feature silently doing nothing. Now the reason
          appears here and the button is still there to try again. */}
      {adviceOffered && !advice.data?.advice ? (
        <section className="card mb-6">
          <h2 className="text-lg font-semibold">{t('magicQuote.review.priceAdviceTitle')}</h2>
          {advice.data?.reason ? (
            <div className="mt-1 mb-2">
              <Alert tone="warning">{advice.data.reason}</Alert>
            </div>
          ) : (
            <p className="mt-1 mb-2 text-sm text-[color:var(--color-muted)]">
              {t('magicQuote.review.priceAdviceOffer')}
            </p>
          )}
          <Button onClick={() => advice.mutate()} busy={advice.isPending}>
            {t('magicQuote.review.priceAdviceAsk')}
          </Button>
        </section>
      ) : null}

      {advice.data?.advice && advice.data.engineTotalCents !== undefined ? (
        <section className="card mb-6">
          <h2 className="text-lg font-semibold">{t('magicQuote.review.priceAdviceTitle')}</h2>
          {/* The engine's number first and in ink, the suggestion beside it and
              visibly secondary: the caveat below says the engine is the one
              that counts, and the layout should not argue with it. */}
          <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <p className="tabular text-sm">
              {t('magicQuote.review.priceAdviceEngine', {
                amount: formatCents(advice.data.engineTotalCents, company?.currencyCode, getDateLocale()),
              })}
            </p>
            <p className="tabular text-sm font-medium text-[color:var(--color-brand-strong)]">
              {t('magicQuote.review.priceAdviceRange', {
                low: formatCents(advice.data.advice.lowCents, company?.currencyCode, getDateLocale()),
                high: formatCents(advice.data.advice.highCents, company?.currencyCode, getDateLocale()),
              })}
            </p>
          </div>
          <ul className="mt-2 list-disc pl-5 text-sm text-[color:var(--color-muted)]">
            {advice.data.advice.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.priceAdviceCaveat')}</p>
        </section>
      ) : null}

      {needsReview.length > 0 ? (
        // Bordered on the warning ramp: this is the pile the screen exists to
        // get through, and it should not look like the four cards around it
        // that are only reporting.
        <section className="card mb-6 border-[color:var(--color-warning)]/40">
          <h2 className="text-lg font-semibold">
            {t('magicQuote.review.needsReviewTitle', { count: needsReview.length })}
          </h2>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.needsReviewBody')}</p>
          <ObservationList
            sessionId={sessionId}
            observations={needsReview}
            canEdit={canReview}
            onShowEvidence={showEvidence}
          />
        </section>
      ) : null}

      {changed.length > 0 ? (
        <section className="card mb-6">
          <h2 className="text-lg font-semibold">{t('magicQuote.review.changedTitle')}</h2>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.changedBody')}</p>
          <ul className="flex flex-col gap-1 text-sm">
            {changed.map((observation) => (
              <li key={observation.id} className="text-[color:var(--color-muted)] line-through">
                {observation.label}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="card mb-6">
        <h2 className="text-lg font-semibold">{t('magicQuote.review.roomsTitle', { count: data.rooms.length })}</h2>
        <p className="text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.roomModifiers')}</p>

        {/* A quote needs at least one priceable room, and the walkthrough
            does not always produce one — four photos with nothing named, a
            house whose rooms match nothing in the library. Without a way to
            say "it's a kitchen" by hand, that walkthrough can never become a
            quote at all, which is a dead end at the last step. */}
        {canReview ? <AddRoomForm sessionId={sessionId} onAdded={invalidate} /> : null}
        {/* "0 rooms" over an empty list is the most confident way this page
            has of saying the walk found nothing, and it is usually not what
            happened — somebody pressed past the wait, or a capture could not
            be read and the organize ran without it. Which of the two it is
            changes what the reader should do: wait, or start adding rooms.

            Asked of work that is actually running, not of `analysisUnfinished`
            — which stays true on a walkthrough whose organize FAILED, because
            a failure leaves no summary. Reading that as "still being read"
            would promise rooms that will appear when it finishes, to somebody
            looking at a failure alert saying nothing is running. */}
        {data.rooms.length === 0 ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">
            {pending > 0 || organizingNow || resynthesize.isPending
              ? t('magicQuote.review.roomsEmptyUnfinished')
              : t('magicQuote.review.roomsEmptyFinished')}
          </p>
        ) : null}
        {/* Ruled rows rather than a bare list: this is the part of the page an
            estimator reads down looking for the room that is wrong, and a
            divider is what makes one line's facts stop belonging to the next. */}
        <ul
          className="mt-3 divide-y divide-[color:var(--color-line)] border-y border-[color:var(--color-line)]"
          // Rules around nothing are two lines with a gap between them. A
          // walkthrough that found no rooms is the ordinary reason to be
          // reading this section, so it should not look like a rendering bug.
          hidden={data.rooms.length === 0}
        >
          {data.rooms.map((room) => (
            <RoomRow key={room.id} sessionId={sessionId} room={room} canEdit={canReview} onChanged={invalidate} />
          ))}
        </ul>
      </section>

      <section className="card mb-6">
        <h2 className="text-lg font-semibold">{t('magicQuote.review.findingsTitle')}</h2>
        {/* Said plainly, because the alternative reading is the one that costs
            trust: a list of claims with no buttons on them looks like a list
            waiting to be actioned. It is not — these are accepted, they are on
            the quote, and the sentence is there so nobody goes hunting for the
            step they are supposed to do next. */}
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.findingsBody')}</p>
        <ObservationList
          sessionId={sessionId}
          observations={live.filter((observation) => !observation.needsReview)}
          canEdit={canReview}
          onShowEvidence={showEvidence}
        />
      </section>

      {can('quotes:write') ? (
        <section className="card mb-6">
          <h2 className="text-lg font-semibold">{t('magicQuote.review.chatTitle')}</h2>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.chatHint')}</p>

          {/* Sided bubbles rather than two weights of the same paragraph. The
              answers here CHANGE the walkthrough — the "applied" list under
              one is work that was added or removed — so which side said what
              has to be unmistakable at a glance. */}
          <ul className="mb-3 flex flex-col gap-2">
            {data.messages.map((message) => (
              <li key={message.id} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={[
                    'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm',
                    message.role === 'user'
                      ? 'rounded-br-sm bg-[color:var(--color-brand)] text-white'
                      : 'rounded-bl-sm bg-[color:var(--color-surface)] text-[color:var(--color-ink)]',
                  ].join(' ')}
                >
                  <p className="whitespace-pre-line">{message.body}</p>
                  {message.applied.length > 0 ? (
                    <ul className="mt-1.5 list-disc border-t border-[color:var(--color-line)] pt-1.5 pl-4 text-sm text-[color:var(--color-muted)]">
                      {message.applied.map((entry) => (
                        <li key={entry}>{entry}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            ))}

            {/* The question, on screen the instant it is sent, with the wait
                made visible under it. A model call runs the better part of a
                minute; a thread that stays empty for that long reads as a
                button that did nothing, and the second press costs another
                action. */}
            {ask.isPending && ask.variables ? (
              <>
                <li className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[color:var(--color-brand)] px-3.5 py-2 text-sm text-white opacity-70">
                    <p className="whitespace-pre-line">{ask.variables}</p>
                  </div>
                </li>
                <li className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-[color:var(--color-surface)] px-3.5 py-2 text-sm text-[color:var(--color-muted)]">
                    <p className="animate-pulse">{t('magicQuote.review.chatThinking')}</p>
                  </div>
                </li>
              </>
            ) : null}
          </ul>

          <TextArea
            label={t('magicQuote.review.chatLabel')}
            rows={2}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder={t('magicQuote.review.chatPlaceholder')}
          />
          <Button
            className="mt-2"
            onClick={() => ask.mutate(question.trim())}
            disabled={!question.trim()}
            busy={ask.isPending}
          >
            {t('magicQuote.review.chatSend')}
          </Button>
        </section>
      ) : null}

      <section className="mb-6">
        <h2 className="mb-2 text-lg font-semibold">{t('magicQuote.review.timelineTitle')}</h2>
        <CaptureTimeline
          sessionId={sessionId}
          captures={data.captures}
          speechRecognitionAvailable={data.speechRecognitionAvailable}
          highlightIds={citedIds}
          openAt={openEvidence}
        />
      </section>
    </WorkPage>
  );
}

/**
 * One room, and everything a person can do about it.
 *
 * The list used to be read-only, which is what made a wrong room permanent. A
 * walkthrough that names a room the library has no type for produces a row
 * that cannot be priced, and until now it could not be given a type, could not
 * be removed, and did not say what was wrong with it — it simply sat there,
 * and Generate refused the whole walkthrough without pointing at it. All three
 * halves of that are fixed here: the reason is on the row, the type is a
 * picker, and Remove exists.
 */
function RoomRow({
  sessionId,
  room,
  canEdit,
  onChanged,
}: {
  sessionId: string;
  room: MagicQuoteRoom;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  /* Room-type names in the reviewer's own language (see localized-text.ts). */
  const localized = useLocalizedContent();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Shares the add-form's cache entry, so opening this screen fetches the
  // library once however many rooms are on it.
  const templates = useQuery({
    queryKey: ['room-templates', 'magic-quote-add'],
    queryFn: () => templateApi.rooms(),
    enabled: canEdit,
  });

  const retype = useMutation({
    mutationFn: (roomTemplateId: string | null) => magicQuoteApi.updateRoom(sessionId, room.id, { roomTemplateId }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const remove = useMutation({
    mutationFn: () => magicQuoteApi.removeRoom(sessionId, room.id),
    onSuccess: () => {
      setConfirming(false);
      onChanged();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  const detail = [
    room.sizeCode,
    room.conditionCode,
    room.estimatedMinutes !== null ? `${room.estimatedMinutes} min` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <li className="py-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{room.name}</span>
          {room.excluded ? (
            <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
              {t('magicQuote.review.roomExcluded')}
            </span>
          ) : null}
          {/* Size, condition and a spoken time all move billable minutes, so
              they are shown rather than applied silently: a misheard "about
              forty minutes" is a number the estimator should be able to see
              before it is priced. */}
          {detail ? <span className="text-[color:var(--color-muted)]">{detail}</span> : null}
        </span>
        <span className="tabular shrink-0 text-[color:var(--color-muted)]">
          {t('magicQuote.review.roomCaptures', { count: room.captureCount })}
        </span>
      </div>

      {/* The sentence generation would have refused with, on the row it is
          about, while there is still something to do about it. */}
      {room.pricingProblem ? (
        <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[color:var(--color-warning-soft)] px-2.5 py-1.5 text-[color:var(--color-warning)]">
          <span aria-hidden="true">⚠</span>
          <span>{t('magicQuote.review.roomWontPrice', { reason: room.pricingProblem })}</span>
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor={`room-type-${room.id}`}>
            {t('magicQuote.review.addRoomType')}
          </label>
          <select
            id={`room-type-${room.id}`}
            className="field-control h-9 w-auto min-w-48 py-1 text-sm"
            style={{ minHeight: '2.25rem' }}
            value={room.roomTemplateId ?? ''}
            disabled={retype.isPending || templates.isLoading}
            onChange={(event) => retype.mutate(event.target.value || null)}
          >
            <option value="">{t('magicQuote.review.addRoomTypeNone')}</option>
            {(templates.data?.items ?? []).map((template) => (
              <option key={template.id} value={template.id}>
                {localized.text(template.name, template.nameTranslations)}
              </option>
            ))}
          </select>
          <Button variant="ghost" className="text-[color:var(--color-danger)]" onClick={() => setConfirming(true)}>
            {t('common.remove')}
          </Button>
        </div>
      ) : null}

      {error ? (
        <div className="mt-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {/* Asked about, because the captures filed under this room lose their
          place when it goes — nothing is deleted, but the walk is re-read. */}
      {confirming ? (
        <ConfirmDialog
          title={t('magicQuote.review.removeRoomTitle', { room: room.name })}
          confirmLabel={t('magicQuote.review.removeRoomConfirm')}
          confirmVariant="danger"
          busy={remove.isPending}
          confirmDisabled={!canEdit}
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirming(false)}
        >
          <p>{t('magicQuote.review.removeRoomBody', { count: room.captureCount })}</p>
        </ConfirmDialog>
      ) : null}
    </li>
  );
}

/**
 * How much of the house the walkthrough actually got, as a bar.
 *
 * The score is the pipeline's own judgment of whether there is enough here to
 * price, and it is the number an estimator is trading away when they generate
 * anyway. Three bands rather than a gradient: what they act on is "go", "look
 * at the gaps first", or "walk it again". The number is always printed beside
 * the bar, so this reads the same with color ignored (spec §27).
 */
function ReadinessMeter({ score }: { score: number }) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(100, score));
  const tone = clamped >= 70 ? '--color-brand' : clamped >= 40 ? '--color-warning' : '--color-danger';
  return (
    <div className="mt-3 flex items-center gap-3">
      <div
        className="h-2 flex-1 overflow-hidden rounded-full bg-[color:var(--color-surface)]"
        role="meter"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('magicQuote.review.readiness', { score: clamped })}
      >
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: `${clamped}%`, backgroundColor: `var(${tone})` }}
        />
      </div>
      <p className="tabular shrink-0 text-sm font-medium">{t('magicQuote.review.readiness', { score })}</p>
    </div>
  );
}

/**
 * The room an estimator adds themselves.
 *
 * Deliberately two fields: what to call it, and which library type prices it.
 * Size and condition have neutral defaults and are editable on the quote,
 * where the estimator can see what they do to the number — asking for them
 * here would be a form standing between a walkthrough and its quote.
 */
function AddRoomForm({ sessionId, onAdded }: { sessionId: string; onAdded: () => void }) {
  const { t } = useTranslation();
  const localized = useLocalizedContent();
  const [name, setName] = useState('');
  const [roomTemplateId, setRoomTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['room-templates', 'magic-quote-add'],
    queryFn: () => templateApi.rooms(),
  });

  const add = useMutation({
    mutationFn: () => magicQuoteApi.addRoom(sessionId, { name: name.trim(), roomTemplateId: roomTemplateId || null }),
    onSuccess: () => {
      setName('');
      setRoomTemplateId('');
      setError(null);
      onAdded();
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <div className="mt-4 rounded-xl border border-dashed border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4">
      <h3 className="text-sm font-semibold">{t('magicQuote.review.addRoomTitle')}</h3>
      <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('magicQuote.review.addRoomBody')}</p>

      {error ? (
        <div className="mt-2">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <TextInput
          label={t('magicQuote.review.addRoomName')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('magicQuote.review.addRoomPlaceholder')}
        />
        <Select
          label={t('magicQuote.review.addRoomType')}
          hint={t('magicQuote.review.addRoomTypeHint')}
          value={roomTemplateId}
          onChange={(event) => setRoomTemplateId(event.target.value)}
          options={[
            { value: '', label: t('magicQuote.review.addRoomTypeNone') },
            ...(templates.data?.items ?? []).map((template) => ({
              value: template.id,
              label: localized.text(template.name, template.nameTranslations),
            })),
          ]}
        />
      </div>
      <Button className="mt-2" disabled={!name.trim()} busy={add.isPending} onClick={() => add.mutate()}>
        {t('magicQuote.review.addRoom')}
      </Button>
    </div>
  );
}

/**
 * Roughly how long the wait takes, so it can be counted down rather than
 * guessed at. The same shape as the finishing overlay's estimate, because it
 * is the same work: a model round trip per unread capture, then the
 * reconciliation, which is the long one.
 */
const SECONDS_PER_UNREAD_CAPTURE = 7;
const SECONDS_TO_ORGANIZE = 40;

/**
 * The page while there is nothing yet worth showing.
 *
 * Shown INSTEAD of the review screen, not above it, which is the whole point.
 * A walkthrough that has not been organized has no rooms, no summary and no
 * findings — and a review screen rendered from that says, in the most
 * confident layout the app has, that the walk found nothing. It did not; it
 * has not been read yet. The two are a world apart to somebody deciding
 * whether to send a price.
 */
function StillReading({
  captureCount,
  pendingCaptureCount,
  organizing,
  startedAt,
  now,
  onShowAnyway,
}: {
  captureCount: number;
  pendingCaptureCount: number;
  organizing: boolean;
  startedAt: number;
  now: number;
  onShowAnyway: () => void;
}) {
  const { t } = useTranslation();
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  const estimate = Math.max(20, pendingCaptureCount * SECONDS_PER_UNREAD_CAPTURE + SECONDS_TO_ORGANIZE);
  const remaining = Math.max(0, estimate - elapsed);
  const percent = Math.min(95, Math.round((elapsed / estimate) * 100));

  return (
    <section className="card text-center" role="status" aria-live="polite">
      <h2 className="text-lg font-semibold">{t('magicQuote.review.stillReadingTitle')}</h2>
      <p className="mt-2 text-sm text-[color:var(--color-muted)]">
        {/* Which half of the work it is on. "Reading the photos" and "working
            out the rooms" are different waits, and the second one is nearly
            over — saying so is the difference between waiting and wondering. */}
        {pendingCaptureCount > 0
          ? t('magicQuote.review.stillReadingCaptures', { count: pendingCaptureCount })
          : organizing
            ? t('magicQuote.review.stillReadingOrganizing', { count: captureCount })
            : t('magicQuote.review.stillReadingStarting')}
      </p>

      <div className="mx-auto mt-5 h-2 max-w-sm overflow-hidden rounded-full bg-[color:var(--color-surface)]">
        <div
          className="h-full rounded-full bg-[color:var(--color-brand)] transition-[width] duration-1000 ease-linear"
          style={{ width: `${Math.max(4, percent)}%` }}
        />
      </div>

      <p className="tabular mt-3 text-sm font-medium">
        {remaining > 0
          ? t('magicQuote.review.stillReadingRemaining', { seconds: remaining })
          : t('magicQuote.review.stillReadingNearlyDone')}
      </p>
      <p className="mt-4 text-xs text-[color:var(--color-muted)]">{t('magicQuote.review.stillReadingSafe')}</p>

      {/* Offered once the estimate has been and gone, not before: a way out
          shown immediately invites pressing it at five seconds, which lands on
          the empty page this screen exists to prevent. Shown late, it is the
          answer to the only question left — "is this ever going to finish?" —
          for the walkthrough whose pass died holding its claim, where the
          honest answer is no, not until the claim lapses. */}
      {elapsed > estimate + 20 ? (
        <Button variant="ghost" className="mt-4" onClick={onShowAnyway}>
          {t('magicQuote.review.stillReadingShowAnyway')}
        </Button>
      ) : null}
    </section>
  );
}
